// Alpha Centauri — closest known star system to Sol, 4.246 light-years away.
//
// Real-world data (sources: NASA Exoplanet Archive, Gaia DR3, ESO):
//   Alpha Centauri A    G2V, 1.2234 R☉, T = 5790 K, mag -0.01
//   Alpha Centauri B    K1V, 0.8632 R☉, T = 5260 K, mag +1.33
//     - A/B binary semi-major axis: 23.52 AU, eccentricity 0.5179,
//       period 79.91 yr (Pourbaix & Boffin 2016)
//   Proxima Centauri    M5.5Ve, 0.1542 R☉, T = 3042 K, mag +11.13
//     - Orbits A/B barycenter at ~13,000 AU on a ~547,000 yr period
//   Proxima d           0.26 M⊕, 0.02885 AU, 5.122 d (Faria 2022)
//   Proxima b           1.07 M⊕, 0.04856 AU, 11.186 d (Anglada-Escudé 2016)
//   Proxima c           7 M⊕,    1.489 AU, 5.21 yr (Damasso 2020)
//
// Scene compression: the real system spans ~13,000 AU edge to edge,
// which translates to roughly 1.8 million scene units at the legacy
// 140 units/AU scale. Compressed to ~1500 units between the A/B
// binary and Proxima so the player can fly between them in seconds
// rather than minutes. Planets stay tight around Proxima (it's a
// small dim red dwarf, so anything in its habitable zone is close).

/** @type {import('../schema.js').StarSystemData} */
export const alphaCentauri = {
  id: 'alpha-centauri',
  name: 'Alpha Centauri',
  description: 'Closest known star system to Sol at 4.246 ly. A triple: the inner binary A and B (G2V plus K1V) at 23.5 AU separation, with the red dwarf Proxima Centauri orbiting the pair at roughly 13,000 AU. Proxima hosts three confirmed planets including Proxima b in its habitable zone.',
  galaxyPosition: { x: 4.246, y: 0, z: 0 },
  spectralClasses: ['G2V', 'K1V', 'M5.5Ve'],
  distanceLY: 4.246,
  // HYG / Hipparcos id of the primary, linking this system to its star on
  // the galaxy map (src/nav/starcatalog.js).
  catalogHip: 71683,
  procedural: null,
  legacy: false,

  // Park Alpha Centauri A 120,000 scene units off Sol along +X. Far
  // enough that Sol's bodies vanish to dots from AC (Pluto at 850
  // units is 141x smaller than this distance — well past the bloom
  // halo). Close enough that GPU float32 transforms still resolve
  // body-scale detail without jitter (worst-case precision at 120k +
  // 36 unit planet orbit lands at ~0.015 unit resolution).
  scenePosition: { x: 120000, y: 0, z: 0 },

  stars: [
    {
      id: 'alpha-a',
      name: 'Alpha Centauri A',
      type: 'Star',
      spectralClass: 'G2V',
      radius: 18,
      temperature: 5790,
      realDiameter: '1,696,000 km',
      description: 'The primary star of the Alpha Centauri triple system. A G2V main-sequence star slightly larger, hotter, and more luminous than the Sun. Forms a binary with Alpha Centauri B in a 79.91-year eccentric orbit.',
      isPrimary: true,
      position: { x: 0, y: 0, z: 0 },
      colors: ['#fff4d6', '#ffd07a'],
      glowColor: '#ffd07a'
    },
    {
      id: 'alpha-b',
      name: 'Alpha Centauri B',
      type: 'Star',
      spectralClass: 'K1V',
      radius: 13,
      temperature: 5260,
      realDiameter: '1,197,000 km',
      description: 'The K1V companion to Alpha Centauri A. Smaller, cooler, and more orange than A. The two stars approach within 11 AU at periastron and recede to 36 AU at apastron, swapping dominance of the sky on any planet hypothetically caught between them.',
      position: { x: 60, y: 0, z: 0 },
      binaryOrbit: {
        hostId: 'alpha-a',
        semiMajorAxis: 60,
        eccentricity: 0.5179,
        period: 79.91,
        orbitalSpeed: 0.0015
      },
      colors: ['#ffc88a', '#ff8e42'],
      glowColor: '#ff9e52'
    },
    {
      id: 'proxima',
      name: 'Proxima Centauri',
      type: 'Star',
      spectralClass: 'M5.5Ve',
      radius: 4,
      temperature: 3042,
      realDiameter: '214,400 km',
      description: 'A small flaring red dwarf, the closest known star to Sol at 4.2465 ly. Orbits the A/B barycenter at roughly 13,000 AU on a 550,000-year path. Hosts three confirmed exoplanets and exhibits frequent stellar flares that complicate the habitability case for its inner worlds.',
      position: { x: 1500, y: 0, z: 0 },
      colors: ['#ff5028', '#c92a08'],
      glowColor: '#ff6a3a'
    }
  ],

  planets: [
    {
      // Alpha Centauri Ab: the viewer's own invented gas giant round Alpha A.
      // No planet is confirmed round A; the 2012 HARPS claim was an
      // Earth-mass planet round B, withdrawn after Rajpaul et al. 2016. It is
      // sized as a hot Jupiter so the system has a centrepiece when you warp
      // in: the Proxima planets are too small to read from arrival distance.
      id: 'alpha-ab',
      name: 'Alpha Centauri Ab',
      type: 'Gas Giant Exoplanet',
      hostStarId: 'alpha-a',
      radius: 6.5,
      // Pushed well past the A/B binary's apastron (alpha-b at
      // periastron-apastron 29..91 from alpha-a, eccentricity 0.518).
      // Visual compression is fine but the planet has to clear the
      // companion star so the geometry doesn't read as overlap.
      distance: 480,
      rotationSpeed: 0.025,
      orbitalSpeed: 0.004,
      eccentricity: 0.06,
      orbitalInclination: 0,
      tilt: 11,
      realDiameter: '~165,000 km (estimated)',
      realDistance: '0.04 AU',
      orbitalPeriod: '3.24 days',
      moons: 0,
      description: 'A hot Jupiter-class gas giant on a tight orbit around Alpha Centauri A. An invented world for the viewer: no planet is confirmed around Alpha Centauri A. The 2012 HARPS claim was an Earth-mass planet around B, withdrawn after Rajpaul et al. 2016. Rendered here as the system\'s visual centerpiece — procedural banding, slow-burning amber atmosphere, no texture.',
      colors: ['#c08858', '#5a2a14'],
      glowColor: '#d49870',
      // Distinct from Nibiru: cooler amber base (closer to "hot
      // atmosphere bleaching toward white at the limb") + warm
      // belt instead of Nibiru's wine. Reads as a sun-baked hot
      // Jupiter rather than a brooding rogue.
      proceduralGasGiant: {
        paletteDeep:  0x4a2a10,    // dark amber-brown
        paletteBelt:  0xb87838,    // warm amber-tan
        paletteZone:  0xf0d4a8,    // pale cream-amber (sun-bleached)
        paletteStorm: 0x6a3010,    // deep molasses (storm centres)
        bandCount:    18.0,
        turbulence:   1.30,
        stormLatDeg:  -14.0,
        stormLonDeg:  55.0,
        stormSize:    0.30,
        limbDarken:   0.85,
        rimGlow:      0.65,
        cloudDetailStrength: 1.4,
        cloudDetailFreq:     8.0,
        bandChaos:           0.75,
        timeScale:           0.30,
        spinRate:            0.045
      }
    },
    {
      id: 'proxima-d',
      name: 'Proxima Centauri d',
      type: 'Sub-Earth Exoplanet',
      hostStarId: 'proxima',
      radius: 0.9,
      distance: 50,
      rotationSpeed: 0.05,
      orbitalSpeed: 0.025,
      eccentricity: 0.04,
      orbitalInclination: 0,
      tilt: 0,
      realDiameter: '~9,000 km (estimated)',
      realDistance: '0.02885 AU',
      orbitalPeriod: '5.12 days',
      moons: 0,
      description: 'A sub-Earth-mass candidate exoplanet, the innermost and smallest world proposed around Proxima Centauri. Scorching surface conditions from a 0.029 AU orbit. Announced by ESO in 2022 as a candidate from new ESPRESSO radial-velocity observations.',
      colors: ['#8a3a20', '#3a1808'],
      glowColor: '#cc4a20'
    },
    {
      id: 'proxima-b',
      name: 'Proxima Centauri b',
      type: 'Terrestrial Exoplanet',
      hostStarId: 'proxima',
      radius: 1.4,
      distance: 95,
      rotationSpeed: 0.008,
      orbitalSpeed: 0.014,
      eccentricity: 0.105,
      orbitalInclination: 0,
      tilt: 5,
      realDiameter: '~14,300 km (estimated)',
      realDistance: '0.04856 AU',
      orbitalPeriod: '11.19 days',
      moons: 0,
      description: 'A terrestrial-mass exoplanet in the habitable zone of Proxima Centauri. The closest known potentially habitable world to Earth. Likely tidally locked given its tight orbit around a red dwarf, with permanent day and night hemispheres. Discovered 2016 (Anglada-Escudé et al.).',
      hasAtmosphere: true,
      atmosphereScale: 1.018,
      atmosphereColor: 0xc8a878,
      atmosphereStrength: 0.4,
      rayleighTint: 0x8a6a4a,
      rayleighStrength: 0.12,
      limbTint: 0xd4b896,
      colors: ['#7a4a30', '#3a2010'],
      glowColor: '#8a5a40'
    },
    {
      id: 'proxima-c',
      name: 'Proxima Centauri c',
      type: 'Super-Earth Exoplanet',
      hostStarId: 'proxima',
      radius: 2.0,
      distance: 280,
      rotationSpeed: 0.006,
      orbitalSpeed: 0.0028,
      eccentricity: 0.04,
      orbitalInclination: 0.05,
      tilt: 18,
      realDiameter: '~22,000 km (estimated)',
      realDistance: '1.489 AU',
      orbitalPeriod: '5.21 years',
      moons: 0,
      description: 'A super-Earth-mass exoplanet on a wide orbit around Proxima Centauri. Frozen surface conditions from the dim red-dwarf flux at 1.5 AU. Proposed in 2020 from radial-velocity data (Damasso et al.); ESO and Faria et al. 2022 still treat it as a candidate.',
      colors: ['#a8b8c8', '#506878'],
      glowColor: '#90a8c0'
    }
  ],

  asteroidBelt: null,
  comets: [],

  summary: {
    starCount: 3,
    planetCount: 4,
    moonCount: 0,
    notableBodies: ['Alpha Cen Ab', 'Proxima b', 'Alpha A']
  }
};
